import { safeAccessibilityRole } from '@/constants/utils/a11y';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Platform, ScrollView, View } from 'react-native';
import { useRouter } from 'expo-router';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import MetaHead from '@/components/MetaHead';
import { pricingSeoMetadata } from '@/content/publicSeoMetadata.cjs';
import PublicFooter from '@/components/PublicFooter';
import Text from '@/components/Typography/Text';
import { pricingPalette } from '@/components/Pricing/pricingPalette';
import PricingClosing from '@/components/Pricing/PricingClosing';
import useLandingStyles from '@/components/Landing/useLandingStyles';
import PricingHero from '@/components/Pricing/PricingHero';
import PricingPlans from '@/components/Pricing/PricingPlans';
import PricingBusiness from '@/components/Pricing/PricingBusiness';
import PricingComparison from '@/components/Pricing/PricingComparison';
import PricingCredits from '@/components/Pricing/PricingCredits';
import PricingFaq from '@/components/Pricing/PricingFaq';
import { isDarkTheme } from '@/constants/Colors';
import { useTheme } from '@/context/ThemeContext';
import { useAuthStore, useModalStore, useToastStore } from '@/store';
import { useGabPurchase } from '@/context/GabPurchaseContext';
import { BILLING, FAQ } from '@/constants/pricingContent';
import { checkIsIapSubscriber, hasValidPlusAccess } from '@/lib/purchaseUtils';
import { trackEvent } from '@/constants/utils/analytics';

/** FAQ markup search engines can read, built from the same source as the page. */
const FAQ_SCHEMA = {
  '@type': 'FAQPage',
  mainEntity: FAQ.map((item) => ({
    '@type': 'Question',
    name: item.question,
    acceptedAnswer: { '@type': 'Answer', text: item.answer },
  })),
};

/**
 * /pricing, art-directed with the same kit as the landing page.
 *
 * Plans, competitor comparison, credits, and the questions that help visitors
 * choose the right subscription.
 */
function PricingPageInner() {
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const [isAnnual, setIsAnnual] = useState(true);
  const [isOpeningCheckout, setIsOpeningCheckout] = useState(false);
  const checkoutPending = useRef(false);

  const { openModal } = useModalStore((state) => state.actions);
  const { addToast } = useToastStore((state) => state.actions);
  const { openCheckout } = useGabPurchase();
  const token = useAuthStore((state) => state.token);
  const isTokenExpired = useAuthStore((state) => state.isTokenExpired);
  const isAuthenticated = Boolean(token) && !isTokenExpired();
  const isPlus = useAuthStore((state) => state.user?.flags?.isPlus);
  const isPlusPremium = useAuthStore((state) => state.user?.flags?.isPlusPremium);
  const isComped = useAuthStore((state) => state.user?.flags?.isComped);
  const plusExpiresAt = useAuthStore((state) => state.user?.plusExpiresAt);
  const activePurchase = useAuthStore((state) => state.user?.activePurchase);
  const isIapSubscriber = useMemo(
    () => checkIsIapSubscriber(activePurchase),
    [activePurchase],
  );
  const hasPlus = useMemo(
    () => hasValidPlusAccess({ isPlus, isComped, plusExpiresAt, purchase: activePurchase }),
    [isPlus, isComped, plusExpiresAt, activePurchase],
  );

  /**
   * Plan cards already show the price and billing period. Keep that selection
   * when opening hosted checkout. iOS/Android choose from store plans instead.
   */
  const handleSelectPlan = useCallback(async (plan) => {
    if (checkoutPending.current) return;
    if (!isAuthenticated) {
      router.push('/signup');
      return;
    }
    if (plan.id !== 'plus' && plan.id !== 'plus-premium') {
      router.push('/app');
      return;
    }
    if ((hasPlus && (plan.id === 'plus' || isPlusPremium)) || isIapSubscriber) {
      // Existing subscriptions belong in Billing; do not stack store and web plans.
      openModal('SettingsModalWeb', { tab: 'subscription' });
      return;
    }
    if (Platform.OS !== 'web' && Platform.OS !== 'macos') {
      openModal('PricingModal');
      return;
    }
    const cadence = isAnnual ? 'yearly' : 'monthly';
    const plusPlanId = plan.id === 'plus-premium' ? `premium-${cadence}` : cadence;
    checkoutPending.current = true;
    setIsOpeningCheckout(true);
    try {
      trackEvent('conversion', 'checkout_started', plusPlanId.replace('yearly', 'annual'));
      await openCheckout({ plusPlanId });
    } catch (error) {
      if (/checkout (?:closed|cancelled|canceled)|user closed|backdrop close/i.test(error?.message || '')) return;
      addToast(error?.message || 'Could not open checkout. Please try again.', 'error');
    } finally {
      checkoutPending.current = false;
      setIsOpeningCheckout(false);
    }
  }, [isAnnual, isAuthenticated, hasPlus, isPlusPremium, isIapSubscriber, openCheckout, openModal, router, addToast]);

  const handleBuyCredits = useCallback(() => {
    if (!isAuthenticated) router.push('/signup');
    else openModal('CreditsPurchaseModal');
  }, [isAuthenticated, openModal, router]);

  return (
    <>
      <PricingHero
        topInset={insets.top}
        isAnnual={isAnnual}
        onBillingChange={setIsAnnual}
      />
      {isOpeningCheckout && <Text accessibilityRole={safeAccessibilityRole('status')} accessibilityLiveRegion="polite">Opening secure checkout…</Text>}
      <PricingPlans isAnnual={isAnnual} onSelectPlan={handleSelectPlan} />
      {Platform.OS === 'web' && <PricingBusiness />}
      <PricingComparison onSelectPlan={handleSelectPlan} />
      <PricingCredits onBuyCredits={handleBuyCredits} />
      <PricingFaq />
      <PricingClosing />
    </>
  );
}

export default function PricingPage() {
  const { theme } = useTheme();
  const isDark = isDarkTheme(theme);

  useLandingStyles();

  const containerStyle = useMemo(() => ({
    flex: 1,
    backgroundColor: pricingPalette(isDark).page,
  }), [isDark]);

  const pricingMetadata = pricingSeoMetadata(`$${Number(BILLING.monthly).toFixed(2)}`, `$${Number(BILLING.annualTotal).toFixed(2)}`);

  return (
    <View style={containerStyle}>
      <MetaHead
        {...pricingMetadata}
        canonical="/pricing"
        keywords="Gab AI pricing, Work mode AI, agentic AI, AI subscription cost, AI credits, AI image generator cost, AI video generator price, ChatGPT alternative price"
        structuredData={{
          '@context': 'https://schema.org',
          '@graph': [
            {
              '@type': 'WebPage',
              name: 'Gab AI Pricing',
              description: pricingMetadata.description,
              url: 'https://gab.ai/pricing',
              mainEntity: {
                '@type': 'Product',
                name: 'Gab AI Plus',
                description: 'Plus access and monthly credits; model, tool and API usage conditions apply.',
                offers: [
                  { amount: BILLING.monthly, duration: 'P1M', name: 'Monthly Plus' },
                  { amount: BILLING.annualTotal, duration: 'P1Y', name: 'Annual Plus' },
                ].map(({ amount, duration, name }) => ({
                  '@type': 'Offer',
                  name,
                  price: String(amount),
                  priceCurrency: 'USD',
                  url: 'https://gab.ai/pricing',
                  priceSpecification: { '@type': 'UnitPriceSpecification', price: String(amount), priceCurrency: 'USD', billingDuration: duration },
                  description: duration === 'P1Y' ? 'Annual amount billed yearly; the monthly equivalent is not the charge.' : 'Monthly amount billed monthly.',
                })),
              },
            },
            FAQ_SCHEMA,
          ],
        }}
      />

      <ScrollView
        {...(Platform.OS === 'web' ? { dataSet: { landingScroll: 'true', pricingPage: 'true' } } : {})}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{ alignItems: 'center' }}
        accessibilityRole={safeAccessibilityRole('main')}
        nativeID="main-content"
        style={{ position: 'relative', zIndex: 1 }}
      >
        <PricingPageInner />
        <PublicFooter variant="editorial" showHero={false} palette={pricingPalette(isDark)} />
      </ScrollView>
    </View>
  );
}
